import React from "react";
import { HardDrive, Database, Film, Image, AudioLines, Server, Box } from "lucide-react";
import { api, fmtBytes } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { usePoll } from "@/hooks/usePoll";
import NoProject from "@/components/NoProject";

const DIR_META = {
  RAW: { c: "#E6B04E", icon: Film, label: "Raw Cache" },
  PROXIES: { c: "#00F0FF", icon: Film, label: "Proxies" },
  THUMBNAILS: { c: "#8B5CF6", icon: Image, label: "Thumbnails" },
  WAVEFORMS: { c: "#10B981", icon: AudioLines, label: "Waveforms" },
  CACHE: { c: "#94A3B8", icon: Box, label: "Working Cache" },
  RENDERS: { c: "#F59E0B", icon: Server, label: "Renders" },
  EXPORTS: { c: "#EF4444", icon: Database, label: "Exports" },
};

export default function StoragePage() {
  const { projectId } = useApp();
  const { data } = usePoll(() => (projectId ? api.storage(projectId) : Promise.resolve(null)), 5000, [projectId]);
  if (!projectId) return <NoProject title="Storage needs a project" />;

  const disk = data?.disk;
  const diskPct = disk ? Math.round((disk.used / disk.total) * 100) : 0;
  const breakdown = data?.breakdown || {};
  const projTotal = data?.project_total || 0;
  const maxDir = Math.max(1, ...Object.values(breakdown));

  return (
    <div className="p-8 fade-up space-y-6">
      <div>
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Storage Management</h1>
        <p className="text-[#94A3B8] text-sm mt-1">Durable media lives in object storage; this shows the local working footprint</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="studio-card p-6 lg:col-span-1">
          <div className="flex items-center gap-2 mb-4"><HardDrive size={18} className="text-[#E6B04E]" /><h3 className="font-head font-bold">Disk</h3></div>
          <div className="relative w-36 h-36 mx-auto mb-4">
            <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#1a1d28" strokeWidth="3" />
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#E6B04E" strokeWidth="3"
                strokeDasharray={`${diskPct} 100`} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-head text-2xl font-extrabold">{diskPct}%</span>
              <span className="metric-label" style={{ fontSize: 9 }}>used</span>
            </div>
          </div>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-[#94A3B8]">Free</span><span className="font-mono text-[#10B981]">{fmtBytes(disk?.free || 0)}</span></div>
            <div className="flex justify-between"><span className="text-[#94A3B8]">Used</span><span className="font-mono">{fmtBytes(disk?.used || 0)}</span></div>
            <div className="flex justify-between"><span className="text-[#94A3B8]">Total</span><span className="font-mono">{fmtBytes(disk?.total || 0)}</span></div>
          </div>
        </div>

        <div className="studio-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-head font-bold">Project Working Footprint</h3>
            <span className="tech-badge">{fmtBytes(projTotal)}</span>
          </div>
          <div className="space-y-4">
            {Object.keys(DIR_META).map((dir) => {
              const meta = DIR_META[dir];
              const Icon = meta.icon;
              const size = breakdown[dir] || 0;
              return (
                <div key={dir} data-testid={`storage-bar-${dir.toLowerCase()}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="flex items-center gap-2 text-sm"><Icon size={14} style={{ color: meta.c }} /> {meta.label}</span>
                    <span className="font-mono text-[12px] text-[#94A3B8]">{fmtBytes(size)}</span>
                  </div>
                  <div className="progress-track">
                    <div className="h-full rounded-full" style={{ width: `${(size / maxDir) * 100}%`, background: meta.c }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
